/**
 * Abonnement vendu sur le site : ventes par prix payé (le test de prix), le
 * découpage site A / site B depuis que les deux variantes le proposent, et
 * revenu annuel d'un abonné comparé aux formules Stripe.
 */

import type { SiteSubscriptionTestSales } from '@shared/types/sales';

import { formatDayShort, formatInteger, formatMoney, formatPercent } from '../lib/format';
import { Card } from './Card';

interface SiteSubscriptionPricesProps {
  site: SiteSubscriptionTestSales;
  currency: string;
}

export function SiteSubscriptionPrices({ site, currency }: SiteSubscriptionPricesProps) {
  const money = (value: number) => formatMoney(value, currency, true);
  const units = site.prices.reduce((sum, price) => sum + price.units, 0);
  // Revenu de la première année d'un abonné, selon sa porte d'entrée.
  const references = [
    { label: 'Site — prix moyen payé', value: site.averagePrice },
    { label: 'Stripe — annuel', value: site.stripeAnnualPrice },
    { label: 'Stripe — mensuel × 12', value: site.stripeMonthlyPricePerYear },
  ];

  return (
    <Card
      title="Prix payés sur le site"
      subtitle={`Abonnements vendus sur Shopify du ${formatDayShort(site.from)} au ${formatDayShort(site.to)}, par prix unitaire TTC remises déduites`}
      className="chart-grid__wide"
    >
      {site.prices.length === 0 ? (
        <p className="empty">Aucun abonnement vendu sur le site dans la période.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="num">Prix payé</th>
                <th className="num">Ventes</th>
                <th className="num">Part</th>
                <th className="num">CA TTC</th>
              </tr>
            </thead>
            <tbody>
              {site.prices.map((price) => (
                <tr key={price.unitPrice}>
                  <td className="num">{price.unitPrice > 0 ? money(price.unitPrice) : 'offert'}</td>
                  <td className="num">{formatInteger(price.units)}</td>
                  <td className="num">{formatPercent(Math.round((price.units / units) * 1000) / 10)}</td>
                  <td className="num">{money(price.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {site.variants.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <caption className="table-caption">
              Par site (variante du test A/B) — offerts exclus
            </caption>
            <thead>
              <tr>
                <th>Site</th>
                <th className="num">Prix</th>
                <th className="num">Commandes de kit</th>
                <th className="num">Avec abonnement</th>
                <th className="num">Attache</th>
                <th className="num">Vendus</th>
              </tr>
            </thead>
            <tbody>
              {site.variants.map((variant) => (
                <tr key={variant.variant}>
                  <td title={`variante ${variant.variant}`}>Site {variant.site}</td>
                  <td className="num">{variant.price !== null ? money(variant.price) : '—'}</td>
                  <td className="num">{formatInteger(variant.kitOrders)}</td>
                  <td className="num">{formatInteger(variant.kitOrdersWithSubscription)}</td>
                  <td className="num">{variant.attachRatePercent !== null ? formatPercent(variant.attachRatePercent) : '—'}</td>
                  <td className="num">{formatInteger(variant.units)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Revenu de la 1re année d'un abonné</th>
              <th className="num">TTC</th>
            </tr>
          </thead>
          <tbody>
            {references.map((reference) => (
              <tr key={reference.label}>
                <td>{reference.label}</td>
                <td className="num">{reference.value !== null ? money(reference.value) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
